import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CalendarRange, CheckCircle2, Clock, FileUp, Loader2, Paperclip, Sparkles, Timer, FileCheck2, ScrollText, Activity, Receipt, PenLine, Landmark, Upload } from 'lucide-react';
import { API_BASE } from '../../api';
import { FolderTree } from './FolderTree';

const MONTH_OPTS = Array.from({ length: 13 }, (_, i) => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
  return { v: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, l: d.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' }) };
});

const get = (t, p) => axios.get(`${API_BASE}/portal/${t}/${p}`).then((r) => r.data);
const MONTH = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString('en-ZA', { month: 'short' });
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const STATUS = {
  done: ['Filed / done', 'bg-emerald-100 text-emerald-800'], in_progress: ['In progress', 'bg-sky-100 text-sky-800'],
  due_soon: ['Due soon', 'bg-amber-100 text-amber-800'], scheduled: ['Scheduled', 'bg-stone-100 text-stone-700'], past: ['Earlier period', 'bg-stone-50 text-stone-400'],
};

export const uploadPortalFile = (token, file, extra = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  Object.entries(extra).forEach(([k, v]) => v != null && fd.append(k, v));
  return axios.post(`${API_BASE}/portal/${token}/uploads`, fd).then((r) => r.data);
};

export const EffortStats = ({ token }) => {
  const [s, setS] = useState(null);
  useEffect(() => { get(token, 'stats').then(setS).catch(() => {}); }, [token]);
  if (!s) return null;
  const items = [[Timer, `${s.hours}h`, 'Hours worked for the current month'], [Landmark, s.filings, 'SARS & CIPC filings done'], [CheckCircle2, s.tasksCompleted, 'Tasks completed'],
    [FileCheck2, s.documents, 'Documents processed'], [PenLine, s.signoffs, 'Reports prepared for you']];
  return (
    <div className="rounded-2xl bg-gradient-to-br from-stone-900 to-stone-800 text-white p-5 sm:p-6" data-testid="portal-effort-stats">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber-300"><Sparkles size={14}/> Our work for you · tax year {s.taxYear}</div>
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-4">
        {items.map(([Icon, v, l]) => (
          <div key={l} className="flex-1 min-w-[130px]"><Icon size={18} className="text-amber-300"/><div className="mt-1.5 text-2xl font-semibold tabular-nums">{v}</div><div className="text-xs text-stone-300">{l}</div></div>
        ))}
      </div>
    </div>
  );
};

const AttachBtn = ({ token, item, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = (f) => { setBusy(true); uploadPortalFile(token, f, { category: 'Other', deadlineKey: item.key, deadlineTitle: item.title }).then(onDone)
    .catch((e) => window.alert(e?.response?.data?.detail || 'Upload failed. Please use a PDF or image.')).finally(() => setBusy(false)); };
  return (
    <>
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { if (e.target.files[0]) pick(e.target.files[0]); e.target.value = ''; }}/>
      <button type="button" onClick={() => ref.current.click()} className="inline-flex items-center gap-1 text-xs text-emerald-800 hover:underline" data-testid={`calendar-attach-${item.key}`}>
        {busy ? <Loader2 size={12} className="animate-spin"/> : <Paperclip size={12}/>} {item.uploads ? `${item.uploads} attached · add` : 'Attach supporting docs'}
      </button>
    </>
  );
};

export const YearCalendar = ({ token }) => {
  const [d, setD] = useState(null);
  const load = () => get(token, 'calendar').then(setD).catch(() => setD({ items: [], months: [] }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);
  if (!d) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div>;
  const yearly = d.items.filter((i) => i.yearly);
  const now = new Date().toISOString().slice(0, 7);
  return (
    <div className="space-y-6" data-testid="portal-calendar">
      <div className="rounded-2xl bg-white border border-stone-200 p-5">
        <div className="font-semibold text-stone-900 flex items-center gap-2"><CalendarRange size={17} className="text-emerald-800"/> Annual obligations · tax year {d.taxYear}</div>
        <p className="text-xs text-stone-500 mt-0.5">The big yearly submissions we manage for you: income tax, provisional tax, EMP501 reconciliations, CIPC and more.</p>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {yearly.length === 0 && <div className="text-sm text-stone-500">No annual submissions on your profile.</div>}
          {yearly.map((i) => (
            <div key={i.key} className="rounded-xl border border-stone-200 p-4 hover:border-emerald-700 transition-colors" data-testid={`calendar-yearly-${i.key}`}>
              <div className="flex items-start justify-between gap-2"><div className="text-sm font-semibold text-stone-900">{i.title}</div><span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS[i.status][1]}`}>{STATUS[i.status][0]}</span></div>
              <div className="mt-1 text-xs text-stone-500 flex items-center gap-1"><Clock size={12}/> Due {fmt(i.dueDate)}</div>
              <div className="mt-2"><AttachBtn token={token} item={i} onDone={load}/></div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-stone-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-stone-100 font-semibold text-stone-900">Month by month</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-stone-100">
          {d.months.map((m) => {
            const list = d.items.filter((i) => (i.dueDate || '').slice(0, 7) === m);
            return (
              <div key={m} className={`bg-white p-4 min-h-[120px] ${m === now ? 'ring-2 ring-inset ring-emerald-600' : ''}`} data-testid={`calendar-month-${m}`}>
                <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-wider text-stone-700">{MONTH(m)} {m.slice(0, 4)}</span>{m === now && <span className="text-[10px] font-semibold text-emerald-700">THIS MONTH</span>}</div>
                <ul className="mt-2 space-y-1.5">
                  {list.length === 0 && <li className="text-xs text-stone-400">Bookkeeping & reviews</li>}
                  {list.map((i) => (
                    <li key={i.key} className="text-xs">
                      <div className="flex items-center gap-1.5"><span className={`h-1.5 w-1.5 rounded-full ${i.status === 'done' ? 'bg-emerald-600' : i.status === 'due_soon' ? 'bg-amber-500' : i.status === 'in_progress' ? 'bg-sky-500' : 'bg-stone-300'}`}/><span className={i.status === 'past' ? 'text-stone-400' : 'text-stone-800'}>{i.title}</span></div>
                      <div className="pl-3 text-[10px] text-stone-400">{fmt(i.dueDate)} · {STATUS[i.status][0]}</div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const ICON = { filing: Landmark, task: CheckCircle2, working: Activity, document: FileCheck2, invoice: Receipt, payment: Receipt, signoff: PenLine, sars: ScrollText, upload: FileUp };
export const ActivityFeed = ({ token }) => {
  const [d, setD] = useState(null);
  useEffect(() => { get(token, 'activity').then(setD).catch(() => setD({ events: [] })); }, [token]);
  if (!d) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div>;
  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-5" data-testid="portal-activity">
      <div className="font-semibold text-stone-900">Work we've done for you</div>
      <p className="text-xs text-stone-500 mt-0.5">A live record of everything happening on your account.</p>
      {d.events.length === 0 && <p className="mt-6 text-sm text-stone-500">Activity will appear here as we work on your account.</p>}
      <ol className="mt-5 relative border-l border-stone-200 ml-3 space-y-4">
        {d.events.map((e, i) => {
          const Icon = ICON[e.type] || CheckCircle2;
          return (
            <li key={i} className="ml-6" data-testid={`activity-${i}`}>
              <span className="absolute -left-3.5 h-7 w-7 rounded-full bg-emerald-50 ring-4 ring-white flex items-center justify-center"><Icon size={14} className="text-emerald-800"/></span>
              <div className="text-sm text-stone-900">{e.title}</div>
              <div className="text-xs text-stone-500">{fmt(e.at)}</div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export const UploadsPanel = ({ token }) => {
  const [d, setD] = useState(null);
  const [tree, setTree] = useState(null);
  const [cat, setCat] = useState('Bank statements');
  const [period, setPeriod] = useState(MONTH_OPTS[1].v);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const ref = useRef(null);
  const load = () => {
    get(token, 'uploads').then(setD).catch(() => setD({ categories: [], uploads: [] }));
    get(token, 'folders').then(setTree).catch(() => setTree(null));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);
  const send = (files) => {
    setBusy(true); setMsg('');
    Promise.all([...files].map((f) => uploadPortalFile(token, f, { category: cat, note, period })))
      .then(() => { setMsg(`${files.length} file(s) uploaded. Thank you!`); setNote(''); load(); })
      .catch((e) => setMsg(e?.response?.data?.detail || 'Upload failed. Use a PDF, photo, Excel, Word or CSV file.')).finally(() => setBusy(false));
  };
  const months = tree ? MONTH_OPTS.filter((m) => {
    const [y, mo] = m.v.split('-').map(Number);
    return !tree.archivedFys.includes(mo <= tree.fyEndMonthNum ? y : y + 1);
  }) : MONTH_OPTS;
  useEffect(() => { if (months.length && !months.some((m) => m.v === period)) setPeriod(months[Math.min(1, months.length - 1)].v); }, [months, period]);
  if (!d) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div>;
  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-5" data-testid="portal-uploads">
      <div className="rounded-2xl bg-white border border-stone-200 p-5 space-y-3 h-fit">
        <div className="font-semibold text-stone-900 flex items-center gap-2"><Upload size={16} className="text-emerald-800"/> Send us documents</div>
        <p className="text-xs text-stone-500">Anything we might need: statements, slips, SARS letters, spreadsheets. We file it by year, type and month for you.</p>
        <label className="block text-xs font-medium text-stone-600">What is it?
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-stone-200 px-3 text-sm bg-white font-normal" data-testid="upload-category-select">
            {d.categories.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="block text-xs font-medium text-stone-600">Which month is it for?
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-stone-200 px-3 text-sm bg-white font-normal" data-testid="upload-month-select">
            {months.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
        </label>
        <label className="block text-xs font-medium text-stone-600">Description (optional)
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} placeholder="e.g. FNB cheque account statements, or what you need us to do with this" className="mt-1 w-full rounded-md border border-stone-200 px-3 py-2 text-sm font-normal" data-testid="upload-note-input"/>
        </label>
        <input ref={ref} type="file" multiple accept="image/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.csv" className="hidden" onChange={(e) => { if (e.target.files.length) send([...e.target.files]); e.target.value = ''; }}/>
        <button type="button" disabled={busy} onClick={() => ref.current.click()} className="w-full rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/50 py-8 text-sm font-medium text-emerald-900 hover:bg-emerald-50 flex flex-col items-center gap-2" data-testid="upload-dropzone-btn">
          {busy ? <Loader2 className="animate-spin"/> : <FileUp size={24}/>} Tap to choose files
          <span className="text-[11px] font-normal text-emerald-800/70">PDF, photos, Excel, Word or CSV · up to 10 MB each</span>
        </button>
        {msg && <p className="text-sm text-stone-700" data-testid="upload-msg">{msg}</p>}
      </div>
      {tree ? <FolderTree data={tree} fileHref={(f) => `${API_BASE}/portal/${token}/uploads/${f.id}/file`} emptyText="Nothing uploaded yet."/> : <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div>}
    </div>
  );
};
